import api from '@services/api';
import { useLanguage } from '@contexts/LanguageContext';
import { getCroppedImg, createFileFromBlob, isCropSupported, getOptimalOutputFormat } from '@utils/imageCropUtils';

export const useAttachmentActions = (
    attachment,
    {
        setIsAIEditing,
        setIsCropping,
        setShowAIEditModal,
        setShowCropModal,
        setImageTimestamp,
        setImageLoaded
    },
    {
        onAIEditComplete,
        onCropComplete,
        onDelete,
        onOrderUpdate
    }
) => {
    const { t } = useLanguage();

    const handleAIEditSubmit = async (params) => {
        try {
            setIsAIEditing(true);

            const requestData = {
                attachment_id: attachment.id,
                prompt: params.prompt,
                replace_original: params.replaceOriginal,
                width: params.width || 1024,
                height: params.height || 1024,
                prompt_upsampling: params.promptUpsampling || false,
                safety_tolerance: params.safetyTolerance || 2,
                output_format: params.outputFormat || 'png'
            };

            if (params.seed !== undefined) {
                requestData.seed = params.seed;
            }

            const response = await api.imagen.editImage(requestData);

            if (response.data.success) {
                // Show success message
                if (window.showNotification) {
                    window.showNotification(t('attachments.aiEditSuccess'), 'success');
                }

                // Close modal
                setShowAIEditModal(false);

                // Notify parent component if callback provided
                if (onAIEditComplete) {
                    onAIEditComplete(response.data);
                }

                // If we replaced the original, we might want to refresh the image
                if (params.replaceOriginal && setImageLoaded) {
                    setImageTimestamp(Date.now());
                    setImageLoaded(false);
                    // Force image reload by updating its src with timestamp
                    setTimeout(() => setImageLoaded(true), 50);
                }
            }
        } catch (error) {
            console.error('AI Edit Error:', error);
            const errorMessage = error.response?.data?.detail || t('attachments.aiEditError');
            if (window.showNotification) {
                window.showNotification(errorMessage, 'error');
            }
        } finally {
            setIsAIEditing(false);
        }
    };

    const handleCropSubmit = async (cropParams, getFileUrlForCrop) => {
        try {
            setIsCropping(true);

            // Get the full image URL without timestamp for crop operation
            const imageUrl = getFileUrlForCrop();

            // Create cropped image blob
            const outputFormat = getOptimalOutputFormat(attachment.file.mime_type);
            const croppedImageBlob = await getCroppedImg(
                imageUrl,
                cropParams,
                outputFormat,
                0.92
            );

            // Create a File object
            const croppedFile = createFileFromBlob(
                croppedImageBlob,
                attachment.file.filename,
                '_cropped'
            );

            // Prepare form data
            const formData = new FormData();
            formData.append('file', croppedFile);
            formData.append('replace_original', 'false'); // Do not replace the original
            formData.append('crop_data', JSON.stringify(cropParams));

            // Upload cropped image
            const response = await api.attachments.cropImage(attachment.id, formData);

            // Check if we got a valid response with attachment data
            if (response.data && response.data.id) {
                // Show success message
                if (window.showNotification) {
                    window.showNotification(t('attachments.crop.success'), 'success');
                }

                // Close modal
                setShowCropModal(false);

                // Notify parent component if callback provided
                if (onCropComplete) {
                    onCropComplete(response.data);
                }

                // Force image reload by updating timestamp and resetting image state
                setImageTimestamp(Date.now());
                setImageLoaded(false);

                // Small delay to ensure the new URL is used
                setTimeout(() => {
                    setImageLoaded(true);
                }, 50);
            } else {
                console.error('Crop failed - unexpected response:', response);
                if (window.showNotification) {
                    window.showNotification(t('attachments.crop.error'), 'error');
                }
            }
        } catch (error) {
            console.error('Crop Error:', error);
            const errorMessage = error.response?.data?.detail || t('attachments.crop.error');
            if (window.showNotification) {
                window.showNotification(errorMessage, 'error');
            }
        } finally {
            setIsCropping(false);
        }
    };

    const handleMoveUp = () => {
        if (onOrderUpdate) {
            onOrderUpdate("up");
        }
    };

    const handleMoveDown = () => {
        if (onOrderUpdate) {
            onOrderUpdate("down");
        }
    };

    const handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onDelete) {
            onDelete();
        }
    };

    const canAIEdit = (editable, isImage) => {
        return attachment && attachment.file && isImage && editable;
    };

    const canCrop = (editable, isImage) => {
        return attachment && attachment.file && isImage &&
               isCropSupported(attachment.file.mime_type) && editable;
    };

    return {
        handleAIEditSubmit,
        handleCropSubmit,
        handleMoveUp,
        handleMoveDown,
        handleDelete,
        canAIEdit,
        canCrop
    };
};
